import { useState } from "react";

// Custom CSS for toggle switches and spacing
const styles = `
  .gap-3 {
    gap: 1rem;
  }
  .gap-2 {
    gap: 0.5rem;
  }
  .form-check-input {
    width: 2em;
    height: 1em;
    position: relative;
    -webkit-appearance: none;
    appearance: none;
    background-color: #ccc;
    border-radius: 1em;
    transition: background-color 0.3s;
    cursor: pointer;
  }
  .form-check-input:checked {
    background-color: #0d6efd; /* Bootstrap primary */
  }
  .form-check-input:checked::before {
    content: "";
    position: absolute;
    width: 0.8em;
    height: 0.8em;
    border-radius: 50%;
    background: white;
    top: 0.1em;
    left: 1.1em;
    transition: left 0.3s;
  }
  .form-check-input::before {
    content: "";
    position: absolute;
    width: 0.8em;
    height: 0.8em;
    border-radius: 50%;
    background: white;
    top: 0.1em;
    left: 0.1em;
    transition: left 0.3s;
  }
`;

export default function AttendancePolicyForm({ onClose }) {
  const [formData, setFormData] = useState({
    policyName: "",
    branch: "",
    description: "",
    workingHours: 8,
    reportingTime: "09:00",
    lateThreshold: "09:15",
    weekends: [],
    holidays: [],
    isFlexible: false,
    requiresApproval: true,
  });

  const [newHoliday, setNewHoliday] = useState("");
  const [currentStep, setCurrentStep] = useState(1);

  const branches = [
    "Head Office",
    "Branch A - Downtown",
    "Branch B - Uptown",
    "Branch C - Suburbs",
    "Branch D - Industrial",
    "Remote Office",
    "All Branches",
  ];

  const weekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // Handle input changes for text, number, and time fields
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "workingHours" ? parseInt(value) || 0 : value,
    }));
  };

  // Handle weekend day toggles
  const handleWeekendToggle = (day) => {
    setFormData((prev) => ({
      ...prev,
      weekends: prev.weekends.includes(day)
        ? prev.weekends.filter((d) => d !== day)
        : [...prev.weekends, day],
    }));
  };

  // Add a new holiday
  const addHoliday = () => {
    if (newHoliday.trim()) {
      setFormData((prev) => ({
        ...prev,
        holidays: [...prev.holidays, newHoliday.trim()],
      }));
      setNewHoliday("");
    }
  };

  // Remove a holiday
  const removeHoliday = (holiday) => {
    setFormData((prev) => ({
      ...prev,
      holidays: prev.holidays.filter((h) => h !== holiday),
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Policy Data:", formData);
    // TODO: Send data to Laravel backend (e.g., axios.post('/api/hr/attendance-policy', formData))
    alert("Attendance policy created successfully!");
    onClose();
  };

  // Navigate between steps
  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 3));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  return (
    <>
      <style>{styles}</style>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title">Create Attendance Policy</h5>
                <p className="small text-muted">
                  Step {currentStep} of 3 - Configure attendance rules for your
                  branch
                </p>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                {/* Step 1: Basic Information */}
                {currentStep === 1 && (
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="bi-geo-alt fs-5 text-primary"></i>
                      <h6 className="fw-semibold">Basic Information</h6>
                    </div>

                    <div className="row row-cols-1 row-cols-md-2 g-3">
                      <div className="col">
                        <label htmlFor="policyName" className="form-label">
                          Policy Name *
                        </label>
                        <input
                          id="policyName"
                          type="text"
                          className="form-control"
                          value={formData.policyName}
                          onChange={(e) =>
                            handleInputChange("policyName", e.target.value)
                          }
                          placeholder="e.g., Main Office Attendance Policy"
                          required
                        />
                      </div>

                      <div className="col">
                        <label htmlFor="branch" className="form-label">
                          Branch *
                        </label>
                        <select
                          id="branch"
                          className="form-select"
                          value={formData.branch}
                          onChange={(e) =>
                            handleInputChange("branch", e.target.value)
                          }
                          required
                        >
                          <option value="" disabled>
                            Select branch
                          </option>
                          {branches.map((branch) => (
                            <option key={branch} value={branch}>
                              {branch}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mt-3">
                      <label htmlFor="description" className="form-label">
                        Description
                      </label>
                      <textarea
                        id="description"
                        className="form-control"
                        value={formData.description}
                        onChange={(e) =>
                          handleInputChange("description", e.target.value)
                        }
                        placeholder="Brief description of this attendance policy..."
                        rows={3}
                      ></textarea>
                    </div>

                    <div className="d-flex justify-content-between">
                      <div className="form-check">
                        <input
                          id="flexible"
                          type="checkbox"
                          className="form-check-input"
                          checked={formData.isFlexible}
                          onChange={(e) =>
                            handleInputChange("isFlexible", e.target.checked)
                          }
                        />
                        <label htmlFor="flexible" className="form-check-label">
                          Flexible working hours
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          id="approval"
                          type="checkbox"
                          className="form-check-input"
                          checked={formData.requiresApproval}
                          onChange={(e) =>
                            handleInputChange(
                              "requiresApproval",
                              e.target.checked
                            )
                          }
                        />
                        <label htmlFor="approval" className="form-check-label">
                          Requires manager approval
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Working Hours */}
                {currentStep === 2 && (
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="bi-clock fs-5 text-primary"></i>
                      <h6 className="fw-semibold">Working Hours & Timing</h6>
                    </div>

                    <div className="row row-cols-1 row-cols-md-3 g-3">
                      <div className="col">
                        <label htmlFor="workingHours" className="form-label">
                          Required Working Hours *
                        </label>
                        <input
                          id="workingHours"
                          type="number"
                          min="1"
                          max="12"
                          className="form-control"
                          value={formData.workingHours}
                          onChange={(e) =>
                            handleInputChange("workingHours", e.target.value)
                          }
                          required
                        />
                        <small className="form-text text-muted">
                          Hours per day (e.g., 8 hours)
                        </small>
                      </div>

                      <div className="col">
                        <label htmlFor="reportingTime" className="form-label">
                          Reporting Time *
                        </label>
                        <input
                          id="reportingTime"
                          type="time"
                          className="form-control"
                          value={formData.reportingTime}
                          onChange={(e) =>
                            handleInputChange("reportingTime", e.target.value)
                          }
                          required
                        />
                        <small className="form-text text-muted">
                          Standard start time
                        </small>
                      </div>

                      <div className="col">
                        <label htmlFor="lateThreshold" className="form-label">
                          Late Threshold *
                        </label>
                        <input
                          id="lateThreshold"
                          type="time"
                          className="form-control"
                          value={formData.lateThreshold}
                          onChange={(e) =>
                            handleInputChange("lateThreshold", e.target.value)
                          }
                          required
                        />
                        <small className="form-text text-muted">
                          Considered late after this time
                        </small>
                      </div>
                    </div>

                    <div className="mt-3">
                      <label className="form-label">Weekend Days</label>
                      <div className="row row-cols-2 row-cols-md-4 g-3">
                        {weekDays.map((day) => (
                          <div
                            key={day}
                            className="col d-flex align-items-center gap-2"
                          >
                            <input
                              id={day}
                              type="checkbox"
                              className="form-check-input"
                              checked={formData.weekends.includes(day)}
                              onChange={() => handleWeekendToggle(day)}
                            />
                            <label
                              htmlFor={day}
                              className="form-label small mb-0"
                            >
                              {day}
                            </label>
                          </div>
                        ))}
                      </div>
                      <small className="form-text text-muted">
                        Select days that are considered weekends for this branch
                      </small>
                    </div>
                  </div>
                )}

                {/* Step 3: Holidays */}
                {currentStep === 3 && (
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="bi-calendar fs-5 text-primary"></i>
                      <h6 className="fw-semibold">Holidays & Special Days</h6>
                    </div>

                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control flex-grow-1"
                        value={newHoliday}
                        onChange={(e) => setNewHoliday(e.target.value)}
                        placeholder="Enter holiday name (e.g., New Year's Day)"
                        onKeyPress={(e) =>
                          e.key === "Enter" &&
                          (e.preventDefault(), addHoliday())
                        }
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addHoliday}
                      >
                        <i className="bi-plus fs-6"></i>
                      </button>
                    </div>

                    <div className="mt-3">
                      <label className="form-label">
                        Added Holidays ({formData.holidays.length})
                      </label>
                      <div className="d-flex flex-wrap gap-2">
                        {formData.holidays.map((holiday, index) => (
                          <span
                            key={index}
                            className="badge bg-secondary text-white d-flex align-items-center gap-1"
                          >
                            {holiday}
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0 ms-1"
                              onClick={() => removeHoliday(holiday)}
                            >
                              <i className="bi-trash fs-6"></i>
                            </button>
                          </span>
                        ))}
                      </div>
                      {formData.holidays.length === 0 && (
                        <p className="small text-muted">
                          No holidays added yet. Add holidays that apply to this
                          branch.
                        </p>
                      )}
                    </div>

                    <div className="card border mt-3">
                      <div className="card-body">
                        <h6 className="card-title mb-2">Policy Summary</h6>
                        <div className="d-flex flex-column gap-2 small">
                          <p>
                            <span className="fw-medium">Policy Name:</span>{" "}
                            {formData.policyName || "Not set"}
                          </p>
                          <p>
                            <span className="fw-medium">Branch:</span>{" "}
                            {formData.branch || "Not selected"}
                          </p>
                          <p>
                            <span className="fw-medium">Working Hours:</span>{" "}
                            {formData.workingHours} hours/day
                          </p>
                          <p>
                            <span className="fw-medium">Reporting Time:</span>{" "}
                            {formData.reportingTime}
                          </p>
                          <p>
                            <span className="fw-medium">Late After:</span>{" "}
                            {formData.lateThreshold}
                          </p>
                          <p>
                            <span className="fw-medium">Weekends:</span>{" "}
                            {formData.weekends.length > 0
                              ? formData.weekends.join(", ")
                              : "None selected"}
                          </p>
                          <p>
                            <span className="fw-medium">Holidays:</span>{" "}
                            {formData.holidays.length} added
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between pt-3 border-top">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                  >
                    Previous
                  </button>

                  <div className="d-flex gap-2">
                    {currentStep < 3 ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={nextStep}
                      >
                        Next Step
                      </button>
                    ) : (
                      <button type="submit" className="btn btn-primary">
                        Create Policy
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
